import { useQueryClient } from '@tanstack/react-query'
import { Check, Info } from 'lucide-react'
import { useContext, useState } from 'react'
import { toast } from 'sonner'

import { getBrokersMembersEnrollmentsQueryKey } from 'components/hooks/needs_attention/useGetBrokersMembersEnrollments'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip'
import { Button } from 'slab'
import {
  getGetBrokersUnprocessedEnrollmentsIdQueryKey,
  useGetBrokersRelevantBrokerages,
} from 'utils/web_api'
import { type BrokersEnrollment } from 'utils/web_api.schemas'

import { cn } from '../../utils'
import BrokersMemberContext from '../context/BrokersMemberContext'
import {
  type EnrollmentMutateOptions,
  useModifyEnrollment,
} from '../hooks/enrollments/useModifyEnrollment'
import { Banner } from '../reusable/Banner'
import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'

type EditEnrollmentBrokerDataProps = {
  enrollment: BrokersEnrollment
}

// EditEnrollmentBrokerData is a component that allows the user to edit
// Submitting Brokerage ID and Brokerage of Record ID for an enrollment.
function EditEnrollmentBrokerData ({
  enrollment,
}: EditEnrollmentBrokerDataProps) {
  const queryClient = useQueryClient()
  const { memberId } = useContext(BrokersMemberContext)

  const modifyEnrollmentMutation = useModifyEnrollment({
    enrollmentId: enrollment.id,
    memberId,
  })

  const { data: brokerages = [] } = useGetBrokersRelevantBrokerages()

  const [submittingBrokerageId, setSubmittingBrokerageId] = useState(
    enrollment.submitting_brokerage_id || ''
  )
  const [brokerageOfRecordId, setBrokerageOfRecordId] = useState(
    enrollment.brokerage_of_record_id || ''
  )
  const [error, setError] = useState<string | null>(null)
  const [submittingOpen, setSubmittingOpen] = useState(false)
  const [brokerageOfRecordOpen, setBrokerageOfRecordOpen] = useState(false)

  const handleSave = () => {
    setError(null)

    const payload = {
      submitting_brokerage_id: submittingBrokerageId,
      brokerage_of_record_id: brokerageOfRecordId,
    }

    const mutationOptions: EnrollmentMutateOptions = {
      onSuccess: (data) => {
        queryClient.setQueryData(
          getGetBrokersUnprocessedEnrollmentsIdQueryKey(enrollment.id),
          data
        )
        queryClient.invalidateQueries({
          queryKey: getBrokersMembersEnrollmentsQueryKey({ memberId }),
        })
        toast.success('Successfully updated brokerage information.', {
          position: 'top-center',
        })
      },
      onError: (err) => {
        setError(`Error updating brokerage information: ${err.message}`)
      },
    }

    modifyEnrollmentMutation.mutate(payload, mutationOptions)
  }

  const shouldShowWarning =
    submittingBrokerageId &&
    brokerageOfRecordId &&
    enrollment.category !== 'general_medical'

  const successContent = (
    <div className='p-4'>
      {error && (
        <Banner type='critical' titleText='Error updating' className='my-2'>
          {error}
        </Banner>
      )}
      {shouldShowWarning && (
        <Banner type='warning' titleText='Category Mismatch' className='my-2'>
          Both brokerage fields are populated but the enrollment category is not
          General Medical. Please ensure the enrollment category is updated to
          General Medical if Thatch will be submitting this enrollment.
        </Banner>
      )}
      <div className='space-y-4'>
        <p>
          These fields are only required if the enrollment is on Thatch. We need
          to set these in order to have the enrollment populate in the
          unprocessed enrollments queue.
        </p>
        <div>
          <label className='w-1/2 flex items-center space-x-1' htmlFor=''>
            <span className='whitespace-nowrap text-ellipsis overflow-hidden'>
              Submitting Brokerage
            </span>
            <span className='flex item-center'>
              <SubmittingBrokerageIDTooltip />
            </span>
          </label>
          <Popover open={submittingOpen} onOpenChange={setSubmittingOpen}>
            <PopoverTrigger asChild>
              <Button
                variant='secondary'
                role='combobox'
                aria-expanded={submittingOpen}
                data-testid='submitting_brokerage_select'
              >
                {submittingBrokerageId
                  ? brokerages.find((b) => b.id === submittingBrokerageId)
                    ?.name || submittingBrokerageId
                  : 'Select brokerage...'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-full p-0'>
              <Command>
                <CommandInput
                  placeholder='Search brokerages...'
                  className='h-9 focus:ring-0 focus:border-transparent border-none outline-none'
                  data-testid='submitting_brokerage_search'
                />
                <CommandList>
                  <CommandEmpty>No brokerage found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value='clear'
                      onSelect={() => {
                        setSubmittingBrokerageId('')
                        setSubmittingOpen(false)
                      }}
                      data-testid='submitting_brokerage_option_clear'
                    >
                      <span className='text-gray-500'>Clear selection</span>
                    </CommandItem>
                    {brokerages.map((brokerage) => (
                      <CommandItem
                        key={brokerage.id}
                        value={`${brokerage.name} ${brokerage.id}`}
                        onSelect={() => {
                          setSubmittingBrokerageId(brokerage.id)
                          setSubmittingOpen(false)
                        }}
                        data-testid={`submitting_brokerage_option_${brokerage.id}`}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            submittingBrokerageId === brokerage.id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        <div className='flex flex-col'>
                          <span>{brokerage.name}</span>
                          <span className='text-xs text-gray-500'>
                            {brokerage.id}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <label className='w-1/2 flex items-center space-x-1'>
            <span className='whitespace-nowrap text-ellipsis overflow-hidden'>
              Brokerage of Record
            </span>
            <span className='flex item-center'>
              <BrokerageOfRecordIDTooltip />
            </span>
          </label>
          <Popover
            open={brokerageOfRecordOpen}
            onOpenChange={setBrokerageOfRecordOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant='secondary'
                role='combobox'
                aria-expanded={brokerageOfRecordOpen}
                data-testid='brokerage_of_record_select'
              >
                {brokerageOfRecordId
                  ? brokerages.find((b) => b.id === brokerageOfRecordId)
                    ?.name || brokerageOfRecordId
                  : 'Select brokerage...'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-full p-0'>
              <Command>
                <CommandInput
                  placeholder='Search brokerages...'
                  className='h-9 focus:ring-0 focus:border-transparent border-none outline-none'
                  data-testid='brokerage_of_record_search'
                />
                <CommandList>
                  <CommandEmpty>No brokerage found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value='clear'
                      onSelect={() => {
                        setBrokerageOfRecordId('')
                        setBrokerageOfRecordOpen(false)
                      }}
                      data-testid='brokerage_of_record_option_clear'
                    >
                      <span className='text-gray-500'>Clear selection</span>
                    </CommandItem>
                    {brokerages.map((brokerage) => (
                      <CommandItem
                        key={brokerage.id}
                        value={`${brokerage.name} ${brokerage.id}`}
                        onSelect={() => {
                          setBrokerageOfRecordId(brokerage.id)
                          setBrokerageOfRecordOpen(false)
                        }}
                        data-testid={`brokerage_of_record_option_${brokerage.id}`}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            brokerageOfRecordId === brokerage.id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        <div className='flex flex-col'>
                          <span>{brokerage.name}</span>
                          <span className='text-xs text-gray-500'>
                            {brokerage.id}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className='flex justify-end'>
          <Button
            onClick={handleSave}
            variant='primary'
            disabled={modifyEnrollmentMutation.isPending}
          >
            {modifyEnrollmentMutation.isPending
              ? 'Saving...'
              : 'Save Brokerage Info'}
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <CollapsibleDataContainer
      heading='Brokerage Information'
      isLoading={modifyEnrollmentMutation.isPending}
      error={error}
      localStorageKey='edit-enrollment-broker-data'
      successContent={successContent}
    />
  )
}

const SubmittingBrokerageIDTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger>
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        Submitting Brokerage ID is the brokerage that is responsible for
        submitting the enrollment. It will show up in that brokerage's
        unprocessed enrollments queue.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const BrokerageOfRecordIDTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger>
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        Brokerage of Record ID matters for automated enrollments where we're
        able to set it.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

export default EditEnrollmentBrokerData
